import React, { useEffect, useMemo, useRef, useState } from 'react';
import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { useViewActive } from '../components/RetainedView.tsx';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import type { YomiriManga, YomiriChapter } from '../../types/extension.ts';
import {
  getHomeRecommendations,
  getRelatedRecommendations,
  readHomeCache,
  openDiscoveryDetails,
  type HomeMedia,
  type HomeRecommendations,
  type MediaKind,
} from '../../metadata/HomeRecommendations.ts';
import { releaseCandidates, normalizeTitle } from '../../metadata/ReleaseSchedule.ts';

import { NyomiriHero } from '../components/home/NyomiriHero.tsx';
import { NyomiriRail, type NyomiriRailItem } from '../components/home/NyomiriRail.tsx';
import { NyomiriContinueMenu } from '../components/home/NyomiriContinueMenu.tsx';
import { NyomiriSkeletonHero } from '../components/skeletons/NyomiriSkeletons.tsx';
import { RecommendationPicker } from '../components/home/RecommendationPicker.tsx';
import '../components/home/home.css';

interface Props {
  onOpenManga: (id: string, chapterId?: string, manga?: YomiriManga, chapter?: YomiriChapter) => void;
  onNavigate: (view: string) => void;
}

interface AnimeHistory {
  animeId: string;
  sourceId: string;
  title: string;
  coverUrl?: string;
  episodeId: string;
  episodeName: string;
  watchedAt: number;
  position?: number;
  duration?: number;
  mediaId?: number;
  discoveryId?: number;
}

async function resolveMediaId(
  rawId: string,
  sourceId: string,
  title: string,
  type: 'ANIME' | 'MANGA',
  explicitMediaId: number | undefined,
  animeLibrary: any[],
  libraryMangas: any[]
): Promise<number> {
  // 1. Explicit mediaId or discoveryId
  if (typeof explicitMediaId === 'number' && Number.isSafeInteger(explicitMediaId) && explicitMediaId > 0) {
    return explicitMediaId;
  }

  // 2. Direct anilist source
  if (typeof explicitMediaId === 'string' && /^\d+$/.test(explicitMediaId)) return Number(explicitMediaId);
  if (sourceId === 'anilist' || /^\d+$/.test(rawId)) {
    const num = Number(rawId);
    if (Number.isSafeInteger(num) && num > 0) return num;
  }

  // 3. Fallback to anime_library or libraryMangas
  const normTitle = normalizeTitle(title || '');
  if (type === 'ANIME') {
    const bySource = animeLibrary.find((e) => e.id === rawId && e.sourceId === sourceId);
    if (bySource?.discoveryId && Number.isSafeInteger(bySource.discoveryId) && bySource.discoveryId > 0) {
      return bySource.discoveryId;
    }
    if (normTitle) {
      const byTitle = animeLibrary.find((e) => e.title && normalizeTitle(e.title) === normTitle);
      if (byTitle?.discoveryId && Number.isSafeInteger(byTitle.discoveryId) && byTitle.discoveryId > 0) {
        return byTitle.discoveryId;
      }
    }
  } else {
    const bySource = libraryMangas.find((m) => m.id === rawId && m.sourceId === sourceId);
    if (bySource?.discoveryId && Number.isSafeInteger(bySource.discoveryId) && bySource.discoveryId > 0) {
      return bySource.discoveryId;
    }
    if (normTitle) {
      const byTitle = libraryMangas.find((m) => m.title && normalizeTitle(m.title) === normTitle);
      if (byTitle?.discoveryId && Number.isSafeInteger(byTitle.discoveryId) && byTitle.discoveryId > 0) {
        return byTitle.discoveryId;
      }
      if (byTitle?.mediaId && Number.isSafeInteger(byTitle.mediaId) && byTitle.mediaId > 0) {
        return byTitle.mediaId;
      }
    }
  }

  // 4. Fallback to cached AniList schedule or discovery feed
  if (title) {
    try {
      const cached = await releaseCandidates(title, type, true);
      if (cached && cached.length > 0 && cached[0]?.id) {
        return cached[0].id;
      }
    } catch {}

    try {
      const cachedFeed = await readHomeCache(type);
      if (cachedFeed) {
        const all = [
          ...(cachedFeed.trending || []),
          ...(cachedFeed.popular || []),
          ...(cachedFeed.topRated || []),
        ];
        const match = all.find((m) => {
          const names = [
            m.title?.romaji,
            m.title?.english,
            m.title?.native,
            ...(m.synonyms || []),
          ].filter(Boolean);
          return names.some((n) => n && normalizeTitle(n) === normTitle);
        });
        if (match?.id) return match.id;
      }
    } catch {}

    // 5. Query live AniList candidates if network is available
    try {
      const live = await releaseCandidates(title, type, false);
      if (live && live.length > 0 && live[0]?.id) {
        return live[0].id;
      }
    } catch {}
  }

  // Source IDs and hashes are not AniList IDs. Keep the source link when unmatched.
  return 0;
}

export function YomiriHomeView({ onOpenManga, onNavigate }: Props) {
  const active = useViewActive();
  const [historyItems, setHistoryItems] = useState<NyomiriRailItem[]>([]);
  const [historyReady, setHistoryReady] = useState(false);
  const [excludedTitles, setExcludedTitles] = useState<string[]>([]);
  const [kind, setKind] = useState<MediaKind>('ANIME');
  const [feed, setFeed] = useState<HomeRecommendations | null>(null);
  const feeds = useRef<Partial<Record<MediaKind, HomeRecommendations>>>({});
  const [related, setRelated] = useState<HomeMedia[]>([]);
  const [chosen, setChosen] = useState<HomeMedia | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);

  // Seed for recommendations: title of last read manga or watched anime
  const [seedTitle, setSeedTitle] = useState<string>('');
  const [hasHistoryHint, setHasHistoryHint] = useState(() => {
    try {
      return localStorage.getItem('yomiri_has_history') === 'true';
    } catch {
      return false;
    }
  });
  const [continueMenu, setContinueMenu] = useState<{
    open: boolean;
    anchorPosition?: { left: number; top: number } | null;
    anchorEl?: HTMLElement | null;
    title: string;
    onContinue: () => void;
    onOpenDetail: () => void;
    onRemove: () => void;
  } | null>(null);

  useEffect(() => {
    if (!active) return;
    let live = true;

    void Promise.all([
      yomiriDatabase.getRecentHistory(30),
      yomiriDatabase.getSetting<AnimeHistory[]>('anime_history', []),
      yomiriDatabase.getLibraryMangas(),
      yomiriDatabase.getSetting<any[]>('anime_library', []),
    ])
      .then(async ([reads, watches, library, animeLib]) => {
        if (!live) return;
        const animeLibrary = animeLib || [];

        // Exclude completed or abandoned titles from general discovery
        setExcludedTitles(
          library
            .filter((row) => row.readingState === 'completed' || row.readingState === 'abandoned')
            .map((row) => row.title)
        );

        // Valid reads (excluding mocks)
        const validReads = reads
          .filter((row) => (row.mangaId || row.chapterId) && !['yomiri-mock-source', 'bridged-mal'].includes(row.sourceId))
          .sort((a, b) => new Date(b.readAt).getTime() - new Date(a.readAt).getTime());

        // Valid anime watches
        const validWatches = (watches || [])
          .filter((row) => row.animeId || row.episodeId)
          .sort((a, b) => b.watchedAt - a.watchedAt);

        // Seed title for "Because you read/watched..."
        const latestManga = validReads[0];
        const latestAnime = validWatches[0];
        if (kind === 'MANGA') {
          setSeedTitle(latestManga?.mangaTitle || '');
        } else {
          setSeedTitle(latestAnime?.title || '');
        }

        type RawHistoryItem =
          | { type: 'MANGA'; read: typeof validReads[0]; time: number; key: string }
          | { type: 'ANIME'; watch: typeof validWatches[0]; time: number; key: string };

        const allRaw: RawHistoryItem[] = [
          ...validReads.map((row) => ({
            type: 'MANGA' as const,
            read: row,
            time: new Date(row.readAt).getTime(),
            key: `manga:${row.sourceId || 'default'}:${row.mangaId || row.chapterId}`,
          })),
          ...validWatches.map((row) => ({
            type: 'ANIME' as const,
            watch: row,
            time: row.watchedAt,
            key: `anime:${row.sourceId || 'default'}:${row.animeId || row.episodeId}`,
          })),
        ].sort((a, b) => b.time - a.time);

        let localDismissed: string[] = [];
        try {
          const stored = localStorage.getItem('yomiri_continue_dismissed');
          if (stored) localDismissed = JSON.parse(stored);
        } catch {}
        const dismissedSet = new Set<string>(localDismissed);
        const seenKeys = new Set<string>();
        const seenWorks = new Set<string>();
        const uniqueRaw: RawHistoryItem[] = [];
        for (const item of allRaw) {
          const title = item.type === 'MANGA' ? item.read.mangaTitle : item.watch.title;
          // Keep edition/season words and numbers; normalize spelling and punctuation only.
          const normalized = title?.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
          const workKey = normalized ? `${item.type}:${normalized}` : item.key;
          if (dismissedSet.has(item.key) || dismissedSet.has(workKey)) {
            continue;
          }
          if (!seenKeys.has(item.key) && !seenWorks.has(workKey)) {
            seenWorks.add(workKey);
            seenKeys.add(item.key);
            uniqueRaw.push(item);
          }
        }
        const topRaw = uniqueRaw.slice(0, 16);

        const resolvedRailItems = await Promise.all(
          topRaw.map(async (entry) => {
            if (entry.type === 'MANGA') {
              const row = entry.read;
              const pct = row.pageCount
                ? Math.min(100, Math.max(0, ((row.lastPageRead + 1) / row.pageCount) * 100))
                : undefined;
              const mediaId = await resolveMediaId(
                row.mangaId,
                row.sourceId,
                row.mangaTitle,
                'MANGA',
                (row as any).mediaId || (row as any).discoveryId,
                animeLibrary,
                library
              );
              const detailUrl = mediaId
                ? `/?section=detail&media=${mediaId}`
                : `/?section=detail&${new URLSearchParams({ manga: row.mangaId, source: row.sourceId }).toString()}`;
              const mangaItemId = `manga:${row.sourceId || 'default'}:${row.mangaId || row.chapterId}`;
              const mangaTitle = row.mangaTitle || 'Manga';
              const mangaSubtitle = row.chapterName || (row.chapterNumber != null ? `Capítulo ${row.chapterNumber}` : undefined);
              const openContinue = () => {
                if (row.mangaId) onOpenManga(row.mangaId, row.chapterId);
              };
              const openDetail = () => {
                history.pushState({ yomiriDetailFrom: location.pathname + location.search }, '', detailUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
              };
              const removeContinue = () => {
                let current: string[] = [];
                try {
                  const stored = localStorage.getItem('yomiri_continue_dismissed');
                  if (stored) current = JSON.parse(stored);
                } catch {}
                const title = row.mangaTitle;
                const normalized = title?.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
                const workKey = normalized ? `MANGA:${normalized}` : entry.key;
                const updated = Array.from(new Set([...current, entry.key, mangaItemId, workKey].filter(Boolean)));
                try {
                  localStorage.setItem('yomiri_continue_dismissed', JSON.stringify(updated));
                } catch {}
                setHistoryItems((prev) => prev.filter((h) => h.id !== mangaItemId && h.id !== entry.key));
              };

              return {
                id: mangaItemId,
                title: mangaTitle,
                coverUrl: row.mangaCoverUrl,
                subtitle: mangaSubtitle,
                format: 'MANGA',
                progress: pct,
                href: detailUrl,
                onClick: (e?: React.MouseEvent<HTMLElement>) => {
                  e?.preventDefault();
                  openDetail();
                },
                onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContinueMenu({
                    open: true,
                    anchorPosition: { left: e.clientX, top: e.clientY },
                    title: mangaTitle,
                    onContinue: openContinue,
                    onOpenDetail: openDetail,
                    onRemove: removeContinue,
                  });
                },
                onOpenMenu: (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContinueMenu({
                    open: true,
                    anchorEl: e.currentTarget,
                    title: mangaTitle,
                    onContinue: openContinue,
                    onOpenDetail: openDetail,
                    onRemove: removeContinue,
                  });
                },
              } as NyomiriRailItem;
            } else {
              const row = entry.watch;
              const pct =
                row.position && row.duration && row.duration > 0
                  ? Math.min(100, Math.max(0, (row.position / row.duration) * 100))
                  : undefined;
              const mediaId = await resolveMediaId(
                row.animeId,
                row.sourceId,
                row.title,
                'ANIME',
                row.mediaId || row.discoveryId,
                animeLibrary,
                library
              );
              const detailUrl = mediaId
                ? `/?section=detail&media=${mediaId}`
                : `/?section=detail&${new URLSearchParams({ anime: row.animeId, source: row.sourceId }).toString()}`;
              const animeItemId = `anime:${row.sourceId || 'default'}:${row.animeId || row.episodeId}`;
              const animeTitle = row.title || 'Anime';
              const animeSubtitle = row.episodeName || (row.episodeId ? `Episodio ${row.episodeId}` : undefined);
              const openContinue = () => {
                if (onNavigate) onNavigate('anime');
                const p = new URLSearchParams({ section: 'anime', anime: row.animeId, animeSource: row.sourceId });
                if (row.episodeId) p.set('episode', row.episodeId);
                history.pushState({}, '', `/?${p.toString()}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              };
              const openDetail = () => {
                history.pushState({ yomiriDetailFrom: location.pathname + location.search }, '', detailUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
              };
              const removeContinue = () => {
                let current: string[] = [];
                try {
                  const stored = localStorage.getItem('yomiri_continue_dismissed');
                  if (stored) current = JSON.parse(stored);
                } catch {}
                const title = row.title;
                const normalized = title?.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
                const workKey = normalized ? `ANIME:${normalized}` : entry.key;
                const updated = Array.from(new Set([...current, entry.key, animeItemId, workKey].filter(Boolean)));
                try {
                  localStorage.setItem('yomiri_continue_dismissed', JSON.stringify(updated));
                } catch {}
                setHistoryItems((prev) => prev.filter((item) => item.id !== animeItemId && item.id !== entry.key));
              };

              return {
                id: animeItemId,
                title: animeTitle,
                coverUrl: row.coverUrl,
                subtitle: animeSubtitle,
                format: 'ANIME',
                progress: pct,
                href: detailUrl,
                onClick: (e?: React.MouseEvent<HTMLElement>) => {
                  e?.preventDefault();
                  openDetail();
                },
                onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContinueMenu({
                    open: true,
                    anchorPosition: { left: e.clientX, top: e.clientY },
                    title: animeTitle,
                    onContinue: openContinue,
                    onOpenDetail: openDetail,
                    onRemove: removeContinue,
                  });
                },
                onOpenMenu: (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContinueMenu({
                    open: true,
                    anchorEl: e.currentTarget,
                    title: animeTitle,
                    onContinue: openContinue,
                    onOpenDetail: openDetail,
                    onRemove: removeContinue,
                  });
                },
              } as NyomiriRailItem;
            }
          })
        );

        if (!live) return;
        setHistoryItems(resolvedRailItems);
        if (resolvedRailItems.length > 0) {
          try { localStorage.setItem('yomiri_has_history', 'true'); } catch {}
          setHasHistoryHint(true);
        } else {
          try { localStorage.removeItem('yomiri_has_history'); } catch {}
          setHasHistoryHint(false);
        }
        setHistoryReady(true);
      })
      .catch(() => {
        if (live) setHistoryReady(true);
      });

    return () => {
      live = false;
    };
  }, [active, kind]);

  // Load Recommendations Feed (trending + popular)
  useEffect(() => {
    if (!active) return;
    let live = true;
    setLoading(true);
    setError('');
    setFeed(feeds.current[kind] || null);

    void readHomeCache(kind).then((cached) => {
      if (live && cached) {
        feeds.current[kind] = cached;
        setFeed(cached);
      }
    });

    void getHomeRecommendations(kind)
      .then((data) => {
        if (live) {
          feeds.current[kind] = data;
          setFeed(data);
        }
      })
      .catch(() => {
        if (live) setError('Las sugerencias no están disponibles temporalmente. Tu lectura sigue acá.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [kind, active, revision]);

  // Related recommendations based on seed
  useEffect(() => {
    setRelated([]);
    if (!active || !seedTitle || !historyReady) return;
    let live = true;

    void getRelatedRecommendations(seedTitle, kind)
      .then((rows) => {
        if (live) setRelated(rows);
      })
      .catch(() => {});

    return () => {
      live = false;
    };
  }, [seedTitle, kind, active, historyReady]);

  // Filter excluded / completed titles
  const normalized = (value: string) =>
    value
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]/gu, '');

  const excluded = new Set(excludedTitles.map(normalized));
  const filterVisible = (rows: HomeMedia[]) =>
    rows.filter(
      (row) =>
        ![row.title.romaji, row.title.english, row.title.native, ...(row.synonyms || [])].some(
          (name) => name && excluded.has(normalized(name))
        )
    );

  const heroTrending = feed ? filterVisible(feed.trending) : [];
  const currentHeroTarget = React.useMemo(() => {
    if (!feed?.trending?.length || !historyItems.length) return null;
    const firstHero = filterVisible(feed.trending)[0];
    if (!firstHero) return null;
    const match = historyItems.find((h) => {
      if (h.href && h.href.includes(`media=${firstHero.id}`)) return true;
      const n1 = normalized(h.title);
      const n2 = normalized(firstHero.title.romaji || firstHero.title.english || '');
      return n1 && n2 && n1 === n2;
    });
    if (match) {
      return {
        episodeOrChapter: match.subtitle || '',
        resumeAction: () => {
          match.onClick();
        },
      };
    }
    return null;
  }, [feed, historyItems]);

  return (
    <Box className="y-home">
      <h1 className="nyomiri-sr-only">Inicio</h1>

      {/* 1. Hero Banner */}
      {feed ? (
        <NyomiriHero items={heroTrending} active={active} onOpen={setChosen} historyTarget={currentHeroTarget} />
      ) : (
        <NyomiriSkeletonHero />
      )}

      {/* 2. Continuar viendo / leyendo */}
      {((!historyReady && hasHistoryHint) || historyItems.length > 0) && (
        <NyomiriRail
          title="Continuar"
          items={historyReady ? historyItems : undefined}
          isLoading={!historyReady}
        />
      )}

      {/* 3. Category Discovery Switcher */}
      <div className="nyomiri-discover-head">
        <h2 className="nyomiri-discover-title">Para descubrir</h2>
        <div className="nyomiri-tabs" role="group" aria-label="Tipo de recomendaciones">
          <button
            type="button"
            className="nyomiri-tab-btn"
            aria-pressed={kind === 'MANGA'}
            onClick={() => setKind('MANGA')}
          >
            <AutoStoriesOutlined sx={{ fontSize: 16 }} />
            Manga
          </button>
          <button
            type="button"
            className="nyomiri-tab-btn"
            aria-pressed={kind === 'ANIME'}
            onClick={() => setKind('ANIME')}
          >
            <PlayCircleOutlineRounded sx={{ fontSize: 16 }} />
            Anime
          </button>
        </div>
      </div>

      {error && (
        <Alert
          severity="info"
          sx={{
            borderRadius: '10px',
            bgcolor: 'var(--bg-card)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border-alpha)',
          }}
          action={
            <Button
              size="small"
              disabled={loading}
              onClick={() => setRevision((v) => v + 1)}
              sx={{ color: 'var(--accent-500)', textTransform: 'none', fontWeight: 600 }}
            >
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* 4. Tendencias */}
      <NyomiriRail
        title="Tendencias"
        mediaItems={feed ? filterVisible(feed.trending) : undefined}
        onOpenMedia={openDiscoveryDetails}
        isLoading={loading && !feed}
      />

      {/* 5. Populares */}
      <NyomiriRail
        title="Los más populares"
        mediaItems={feed ? filterVisible(feed.popular) : undefined}
        onOpenMedia={openDiscoveryDetails}
        isLoading={loading && !feed}
      />

      {/* 6. Porque leíste / viste... (Only shown when there's a seed work) */}
      <NyomiriRail title="Mejor valorados" mediaItems={feed?.topRated?filterVisible(feed.topRated):undefined} onOpenMedia={openDiscoveryDetails} isLoading={loading&&!feed}/>
      {seedTitle && related.length > 0 && (
        <NyomiriRail
          title={`Porque ${kind === 'MANGA' ? 'leíste' : 'viste'} ${seedTitle}`}
          mediaItems={filterVisible(related)}
          onOpenMedia={openDiscoveryDetails}
        />
      )}

      {/* Footer Note */}
      {!!feed && (
        <p className="nyomiri-home-credit">
          Sugerencias y puntuaciones provistas por{' '}
          <a href="https://anilist.co" target="_blank" rel="noreferrer">
            AniList
          </a>
          . La disponibilidad se comprueba en tus fuentes instaladas.
        </p>
      )}

      {/* Source Match Picker Dialog */}
      <RecommendationPicker
        item={chosen}
        onClose={() => setChosen(null)}
        onOpenManga={onOpenManga}
        onNavigate={onNavigate}
      />
      {continueMenu && (
        <NyomiriContinueMenu
          open={continueMenu.open}
          anchorPosition={continueMenu.anchorPosition}
          anchorEl={continueMenu.anchorEl}
          title={continueMenu.title}
          onClose={() => setContinueMenu(null)}
          onContinue={continueMenu.onContinue}
          onOpenDetail={continueMenu.onOpenDetail}
          onRemove={continueMenu.onRemove}
        />
      )}
    </Box>
  );
}
